import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import Button from "react-bootstrap/Button"
import { FiArchive } from "react-icons/fi"
import { ConfirmDialog, ListItem } from "components"
import Loading from "components/Loading"
import { customerAdministration, members } from "state_management"
import useT from "utils/hooks/useT"
import "./TeamMembersConfig.scss"

const QA_MANAGER = "qa-manager"

// SPEC-057 L3 — the customer admin's QA-administration surface for ONE LIMS team: appoint or
// remove the team's QA Manager (the root of the quality column appoints its head; unsigned by
// design, but every change lands in the team's LIMS audit trail server-side), and archive a
// departing QA Manager (the L1 archive matrix row that belongs to the customer admin). Members
// are read via the dedicated customer-admin read path — the actor is team-less, so the ordinary
// GET /members can never serve them. The popup only ever opens for LIMS teams (team.showLims)
// and only for a CUSTOMER_ADMIN actor.
export default function TeamMembersConfig({ team }) {
  const dispatch = useDispatch()
  const { t } = useT("common")
  const [teamMembers, setTeamMembers] = useState(null)
  const [activeArchive, setActiveArchive] = useState(null)

  useEffect(() => {
    dispatch(customerAdministration.fetchTeamMembers(team.id))
      .then((items) => setTeamMembers(items.filter((member) => !member.archived)))
      .catch(() => setTeamMembers([]))
  }, [dispatch, team.id])

  if (teamMembers === null) {
    return <Loading />
  }

  const isQaManager = (member) => (member.qcCapabilities || []).includes(QA_MANAGER)

  const applyCapabilities = (item) => {
    setTeamMembers((current) =>
      current.map((member) => (member.id === item.memberId ? { ...member, qcCapabilities: item.capabilities } : member))
    )
  }

  const handleGrant = (member) => {
    dispatch(customerAdministration.grantQaManager(member.id)).then(applyCapabilities).catch(() => {})
  }

  const handleRevoke = (member) => {
    dispatch(customerAdministration.revokeQaManager(member.id)).then(applyCapabilities).catch(() => {})
  }

  const handleArchive = () => {
    const target = activeArchive
    const withoutTarget = (current) => current.filter((member) => member.id !== target.id)
    dispatch(members.archive(target.id))
      .then(() => setTeamMembers(withoutTarget))
      // A refusal is toasted by the message layer — just close the dialog either way.
      .catch(() => {})
      .finally(() => setActiveArchive(null))
  }

  return (
    <div className="TeamMembersConfig-container">
      <div className="TeamMembersConfig-title">
        <p>{t("teamMembersTitle", { name: team.name })}</p>
      </div>
      <p className="TeamMembersConfig-info">{t("limsQaManagerAdminInfo")}</p>
      <div className="TeamMembersConfig-list">
        {teamMembers.map((member) => (
          <ListItem text={`${member.initials} — ${member.email}`} key={member.id}>
            {isQaManager(member) && <span className="TeamMembersConfig-badge">{t("limsCapQaManager")}</span>}
            {isQaManager(member) ? (
              <>
                <Button variant="outline-danger" size="sm" type="button" onClick={() => handleRevoke(member)}>
                  {t("removeQaManager")}
                </Button>
                <FiArchive
                  className="admin-icons-hovereffect"
                  title={t("archiveMember")}
                  onClick={() => setActiveArchive(member)}
                />
              </>
            ) : (
              <Button variant="outline-primary" size="sm" type="button" onClick={() => handleGrant(member)}>
                {t("appointQaManager")}
              </Button>
            )}
          </ListItem>
        ))}
      </div>
      {activeArchive && (
        <ConfirmDialog onCancel={() => setActiveArchive(null)} onConfirm={handleArchive}>
          <h4>{t("confirmArchivalOfMember", { initials: activeArchive.initials })}</h4>
        </ConfirmDialog>
      )}
    </div>
  )
}
